import React from 'react';
import { Moon, Sun } from 'lucide-react';
import { clsx } from 'clsx';
import { useTheme } from '../hooks/useTheme';

interface ThemeToggleProps {
  className?: string;
  labelLight?: string;
  labelDark?: string;
}

/**
 * Manual light/dark override.
 *
 * The site normally picks its mode from the visitor's local clock (06:00-17:59
 * light). Tapping this pins the opposite mode and remembers it; long-pressing
 * (or a second tap once pinned to the clock's own answer) is not a separate
 * control — visitors return to the clock by choosing the mode the clock wants.
 */
const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className,
  labelLight = 'Switch to light mode',
  labelDark = 'Switch to dark mode',
}) => {
  const { mode, toggle } = useTheme();
  const goingDark = mode === 'light';

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={goingDark ? labelDark : labelLight}
      title={goingDark ? labelDark : labelLight}
      className={clsx(
        'w-9 h-9 flex items-center justify-center rounded-lg',
        'bg-fill/60 border border-separator text-ink-secondary',
        'hover:text-ink-primary hover:bg-fill transition-colors duration-200',
        'focus:outline-none focus:ring-2 focus:ring-ring/50',
        className,
      )}
    >
      {goingDark ? <Moon size={16} /> : <Sun size={16} />}
    </button>
  );
};

export default ThemeToggle;
